import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import {
  ApplicationService,
  DuplicateApplicationInfo,
  DuplicateCheckOptions,
  extractDuplicateApplication,
} from '../../services/application.service';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
} from '@angular/forms';
import { PasteTextareaComponent } from '../shared/paste-textarea/paste-textarea.component';
import { DuplicateApplicationDialogComponent } from '../shared/duplicate-application-dialog/duplicate-application-dialog.component';
import { TranslationService } from '../../services/translation.service';
import type { JobDescriptionStepTranslations } from '../../i18n/job-description-step';
import en from '../../i18n/en/job-description-step';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-job-description-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PasteTextareaComponent,
    DuplicateApplicationDialogComponent,
  ],
  templateUrl: './job-description-step.component.html',
  styleUrl: './job-description-step.component.css',
})
export class JobDescriptionStepComponent {
  @Input() isInsideWizard = false;
  @Output() closeWizardRequest = new EventEmitter<void>();

  jobDescriptionForm: FormGroup;
  jobUrlForm: FormGroup;
  loading = false;
  formattedDescription = '';
  errorMessage: string | null = null;
  urlErrorMessage: string | null = null;
  /** Set instead of errorMessage/urlErrorMessage for a 402 (agent.applyJob.total
   * lifetime limit reached) or 403 (agent.applyJob capability off for this
   * plan) - both are "upgrade your plan" situations with their own CTA, not
   * the generic retry-or-fallback messages. Same pattern as
   * root-cv-start.component.ts. */
  planLimitReached = false;
  /** chamba-ai#503: the backend answered 409 duplicate_application - the
   * application the user already has for this job, shown in a dialog that lets
   * them open it or create another one anyway. `duplicate` is kept after the
   * dialog closes so its content doesn't blank out mid-animation. */
  duplicate: DuplicateApplicationInfo | null = null;
  duplicateDialogVisible = false;
  /** "Create anyway" is in flight: the dialog's actions are disabled. */
  duplicateBusy = false;
  /** The request that got the 409, repeated as-is (plus allow_duplicate) if the
   * user chooses to create anyway - not whatever the textarea holds by then. */
  private pendingSubmit: { kind: 'url' | 'text'; value: string } | null = null;
  translations: JobDescriptionStepTranslations = en;
  /** What the single smart input currently holds - replaces the old
   * "Paste text" / "Paste URL" tabs. 'url' routes Submit through
   * onSubmitFromUrl(), anything else through the plain-text onSubmit(). */
  inputKind: 'empty' | 'url' | 'text' = 'empty';
  /** Translations handed to <app-paste-textarea> (OnPush) - rebuilt only when
   * the language or inputKind changes, so the input keeps a stable identity. */
  pasteTranslations: Record<string, string> = this.buildPasteTranslations();

  get t(): JobDescriptionStepTranslations {
    return this.translations;
  }

  get detectedUrlText(): string {
    let host = '';
    try {
      host = new URL(this.jobUrlForm.value.jobUrl).hostname.replace(/^www\./, '');
    } catch {
      // inputKind is only 'url' for parseable URLs - nothing to show otherwise.
    }
    return this.t.DETECTED_URL.replace('{host}', host);
  }

  constructor(
    private applicationService: ApplicationService,
    private fb: FormBuilder,
    private translationService: TranslationService,
    private router: Router,
    private messageService: MessageService
  ) {
    this.jobDescriptionForm = this.fb.group({
      jobDescription: ['', Validators.required],
    });

    this.jobUrlForm = this.fb.group({
      jobUrl: ['', [Validators.required, this.urlValidator.bind(this)]],
    });

    this.translations =
      this.translationService.getJobDescriptionStepTranslations() || en;

    this.translationService.onChange(() => {
      this.translations =
        this.translationService.getJobDescriptionStepTranslations() || en;
      this.pasteTranslations = this.buildPasteTranslations();
      // If error was shown, keep it consistent with the selected language
      if (this.jobDescriptionForm.invalid) {
        this.errorMessage = this.t.REQUIRED_ERROR;
      }
    });
  }

  /**
   * Simple URL validator - checks basic URL format
   */
  private urlValidator(control: FormControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    try {
      new URL(control.value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  }

  /**
   * A lone http(s)/www link (no other text around it) is treated as a job URL
   * to fetch; anything else - including a pasted description that merely
   * contains a link - is sent as text. Returns the normalized URL or null.
   */
  static detectJobUrl(raw: string | null | undefined): string | null {
    const value = (raw || '').trim();
    if (!value || /\s/.test(value) || !/^(https?:\/\/|www\.)/i.test(value)) {
      return null;
    }
    const candidate = /^www\./i.test(value) ? `https://${value}` : value;
    try {
      const url = new URL(candidate);
      return url.hostname.includes('.') ? candidate : null;
    } catch {
      return null;
    }
  }

  private buildPasteTranslations(): Record<string, string> {
    return {
      ...this.t,
      SUBMITTING: this.inputKind === 'url' ? this.t.URL_FETCHING : this.t.SUBMITTING,
    };
  }

  // Getter to safely get the FormControl for the shared component
  get jobDescriptionControl(): FormControl {
    return this.jobDescriptionForm.get('jobDescription') as FormControl;
  }

  onJobDescriptionChange(value: string): void {
    this.jobDescriptionForm.get('jobDescription')?.setValue(value);

    const url = JobDescriptionStepComponent.detectJobUrl(value);
    this.jobUrlForm.get('jobUrl')?.setValue(url || '');
    const kind = url ? 'url' : value?.trim() ? 'text' : 'empty';
    if (kind !== this.inputKind) {
      this.inputKind = kind;
      this.pasteTranslations = this.buildPasteTranslations();
      // A stale error from the other mode would be misleading now.
      this.errorMessage = null;
      this.urlErrorMessage = null;
    }
  }

  isInvalid(controlName: string): boolean {
    const control = this.jobDescriptionForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  clearForm(): void {
    this.jobDescriptionForm.reset();
    this.jobUrlForm.reset();
    this.errorMessage = null;
    this.urlErrorMessage = null;
    this.planLimitReached = false;
    this.inputKind = 'empty';
    this.pasteTranslations = this.buildPasteTranslations();
  }

  goToPlans(): void {
    this.router.navigate(['/home/plans']);
  }

  private showPlanLimitReached(): void {
    this.planLimitReached = true;
    this.messageService.add({
      severity: 'warn',
      summary: this.t.ERROR_PLAN_LIMIT,
      detail: this.t.ERROR_PLAN_LIMIT_CTA,
      life: 8000,
    });
  }

  onSubmit() {
    if (this.inputKind === 'url') {
      this.onSubmitFromUrl();
      return;
    }
    if (this.jobDescriptionForm.invalid) {
      this.errorMessage = this.t.REQUIRED_ERROR;
      return;
    }
    this.loading = true;
    this.errorMessage = null;
    this.planLimitReached = false;
    this.requestFromText(this.jobDescriptionForm.value.jobDescription);
  }

  onSubmitFromUrl() {
    if (this.jobUrlForm.invalid) {
      this.urlErrorMessage = this.t.URL_INVALID_ERROR;
      return;
    }
    this.loading = true;
    this.urlErrorMessage = null;
    this.planLimitReached = false;
    this.requestFromUrl(this.jobUrlForm.value.jobUrl.trim());
  }

  private requestFromText(text: string, options?: DuplicateCheckOptions): void {
    this.pendingSubmit = { kind: 'text', value: text };
    const payload = {
      message_type: 'from_user',
      message_text: text,
    };
    this.applicationService.addApplication(payload, options).subscribe({
      next: (result) => {
        this.closeDuplicateDialog();
        this.loading = false;
        console.log(result);
        if (
          typeof result.application_id === 'number' &&
          !isNaN(result.application_id)
        ) {
          this.applicationService.setCurrentApplicationId(
            result.application_id
          );
        } else {
          this.errorMessage = this.t.UNEXPECTED_RESPONSE;
        }
      },
      error: (err) => {
        this.loading = false;
        if (this.handleDuplicate(err)) {
          return;
        }
        if (err?.status === 402 || err?.status === 403) {
          this.showPlanLimitReached();
        } else {
          this.errorMessage = this.t.SUBMIT_FAILED;
        }
      },
    });
  }

  private requestFromUrl(jobUrl: string, options?: DuplicateCheckOptions): void {
    this.pendingSubmit = { kind: 'url', value: jobUrl };
    this.applicationService.addApplicationFromJobUrl(jobUrl, options).subscribe({
      next: (result) => {
        this.closeDuplicateDialog();
        this.loading = false;
        console.log(result);
        if (
          typeof result.application_id === 'number' &&
          !isNaN(result.application_id)
        ) {
          const applicationId = result.application_id;
          this.applicationService.setCurrentApplicationId(applicationId);

          // chamba-ai#332: the user already typed this URL, but the
          // extraction runs an LLM over the fetched page's *content* and
          // never returns/stores the URL itself - without this, the job
          // posting's url field stays empty and the user has to paste it
          // again manually into job-posting.component.ts's inline-edit
          // field (which already exists and already works). Best-effort:
          // the application was already created successfully regardless,
          // so a failure here must not surface as a fetch error.
          //
          // chamba-ai#503: after "create anyway" the user already accepted
          // that another application has this URL, so this follow-up must not
          // trip the duplicate check (a 409 here would reach the global toast).
          this.applicationService.updateJobPosting(applicationId, { url: jobUrl }, {
            allowDuplicate: options?.allowDuplicate,
          }).subscribe({
            error: () => {
              // Ignore - worst case the user re-types the URL manually,
              // exactly today's behavior.
            },
          });

          this.messageService.add({
            severity: 'success',
            summary: this.t.URL_FETCH_SUCCESS_SUMMARY,
            detail: this.t.URL_FETCH_SUCCESS_DETAIL,
            life: 6000,
          });
        } else {
          this.urlErrorMessage = this.t.UNEXPECTED_RESPONSE;
        }
      },
      error: (err) => {
        this.loading = false;
        if (this.handleDuplicate(err)) {
          return;
        }
        if (err.name === 'TimeoutError') {
          this.urlErrorMessage = this.t.URL_FETCH_TIMEOUT;
        } else if (err.status === 0 || err.error instanceof ErrorEvent) {
          this.urlErrorMessage = this.t.URL_FETCH_NETWORK_ERROR;
        } else if (err.status === 402 || err.status === 403) {
          this.showPlanLimitReached();
        } else if (err.status === 422) {
          this.urlErrorMessage = this.t.URL_FETCH_FAILED + ' ' + this.t.URL_FETCH_FAILED_FALLBACK;
        } else if (err.status >= 500) {
          this.urlErrorMessage = this.t.URL_FETCH_SERVER_ERROR;
        } else {
          this.urlErrorMessage = this.t.SUBMIT_FAILED;
        }
      },
    });
  }

  // --- chamba-ai#503: "you already have an application for this job" ---

  /** True (and the dialog is open) when `err` is a 409 duplicate_application.
   * Otherwise closes a "create anyway" dialog that was waiting on this request,
   * so its failure shows through the regular error handling below the input. */
  private handleDuplicate(err: unknown): boolean {
    const duplicate = extractDuplicateApplication(err);
    if (!duplicate) {
      this.closeDuplicateDialog();
      return false;
    }
    this.duplicate = duplicate;
    this.duplicateBusy = false;
    this.duplicateDialogVisible = true;
    return true;
  }

  private closeDuplicateDialog(): void {
    this.duplicateDialogVisible = false;
    this.duplicateBusy = false;
  }

  /** "Open it": go to the application the user already has. */
  onDuplicateOpen(): void {
    const id = this.duplicate?.id;
    this.closeDuplicateDialog();
    this.pendingSubmit = null;
    if (id != null) {
      // No detail route exists: an application opens through ?applicationId=
      // on Quick Application or on the wizard, whichever this step lives in.
      this.router.navigate([this.isInsideWizard ? '/home/wizard' : '/home/quick-application'], {
        queryParams: { applicationId: id },
      });
    }
  }

  /** "Create anyway": repeat the request that got the 409, knowingly. */
  onDuplicateConfirm(): void {
    const pending = this.pendingSubmit;
    if (!pending || this.duplicateBusy) {
      return;
    }
    this.duplicateBusy = true;
    this.loading = true;
    this.errorMessage = null;
    this.urlErrorMessage = null;
    this.planLimitReached = false;
    if (pending.kind === 'url') {
      this.requestFromUrl(pending.value, { allowDuplicate: true });
    } else {
      this.requestFromText(pending.value, { allowDuplicate: true });
    }
  }

  /** "Cancel" (or X / Escape): close it and leave what the user pasted as it was. */
  onDuplicateCancel(): void {
    if (this.duplicateBusy) {
      return;
    }
    this.closeDuplicateDialog();
    this.pendingSubmit = null;
  }
}
